import type { BackupFile, BackupValidationResult } from './backupSerialize';
import { validateBackupFile } from './backupSerialize';
import { driveFilesList, driveUploadJson, driveDownloadFile, driveDeleteFile, type DriveFile } from './driveClient';
import { findOrCreateBackupFolder, findExistingBackupFolder, APP_PROPERTY_KEY, APP_PROPERTY_VALUE } from './driveFolder';

/**
 * ============================================================================
 * GOOGLE DRIVE — BACKUP ORCHESTRATION (Stage 3b)
 * ============================================================================
 *
 * Ties together the existing local backup format (backupSerialize.ts,
 * UNCHANGED — there is only ever one backup format) with the Drive REST
 * client and folder logic. This file contains no serialization logic of
 * its own and — deliberately — no database access: it receives an
 * already-built BackupFile (the caller, RecordsPage.tsx's Backup & Restore section, builds it with
 * the same getLoan()/listEvents()/createBackup() sequence the local
 * "Download Backup" button uses) and only handles the Drive transport.
 * Keeping Dexie out of this file is what makes the upload/list/download
 * logic directly unit-testable under plain `tsx` with a stubbed fetch,
 * consistent with how createBackup() takes dexieSchemaVersion as a
 * parameter rather than reading the database itself.
 *
 * Retention (Stage 3D) lives here because it is a property of the Drive
 * backup SET, not of who triggered a given upload: both manual and
 * automatic uploads go through uploadBackupWithRetention(). Automatic
 * scheduling (Stage 3C) lives in driveAutoBackup.ts.
 */

export function driveBackupFileName(exportedAtIso: string): string {
  return `LIC-HFL-Loan-Backup-${exportedAtIso.slice(0, 10)}.json`;
}

export interface DriveBackupUploadResult {
  fileId: string;
  fileName: string;
  /** The dedicated folder the file was uploaded into — retention lists exactly this folder. */
  folderId: string;
}

const BACKUP_MIME_TYPE = 'application/json';

/**
 * Uploads an already-built backup into the dedicated Drive folder
 * (found or created via findOrCreateBackupFolder). The uploaded content
 * is exactly JSON.stringify(backup) — the same object the local
 * download path serializes; no second schema. Resolves only once Drive
 * has confirmed the file was created with a real file id (see
 * driveUploadJson) — a network-level "ok" alone is not treated as
 * success.
 */
export async function uploadBackupToDrive(accessToken: string, backup: BackupFile): Promise<DriveBackupUploadResult> {
  const folderId = await findOrCreateBackupFolder(accessToken);
  const file = await driveUploadJson(
    accessToken,
    {
      name: driveBackupFileName(backup.exportedAt),
      // Stated explicitly (not left to Drive's content sniffing) because
      // retention only ever deletes files whose type is exactly this.
      mimeType: BACKUP_MIME_TYPE,
      parents: [folderId],
      appProperties: { [APP_PROPERTY_KEY]: APP_PROPERTY_VALUE },
    },
    JSON.stringify(backup)
  );
  return { fileId: file.id, fileName: file.name, folderId };
}

export interface DriveBackupListItem {
  fileId: string;
  fileName: string;
  createdTime?: string;
}

/**
 * Lists only the backup files inside the dedicated backup folder —
 * never a broader Drive search. If the folder doesn't exist yet (no
 * backup has ever been made), returns an empty list rather than
 * creating a folder just to list it: listing is read-only, and folder
 * creation only ever happens as a side effect of an upload.
 */
export async function listDriveBackups(accessToken: string): Promise<DriveBackupListItem[]> {
  const folder = await findExistingBackupFolder(accessToken);
  if (!folder) {
    return [];
  }

  const files: DriveFile[] = await driveFilesList(
    accessToken,
    `'${folder.id}' in parents and trashed = false`,
    'files(id,name,createdTime)'
  );

  return files
    .map((f) => ({ fileId: f.id, fileName: f.name, createdTime: f.createdTime }))
    .sort((a, b) => (b.createdTime ?? '').localeCompare(a.createdTime ?? ''));
}

/**
 * Downloads a backup file's content and validates it with the EXISTING
 * validateBackupFile() — the same function local file-based restore
 * uses. Never touches local data; the caller is responsible for the
 * explicit user confirmation step and for calling the existing
 * restoreBackup() only after that confirmation.
 */
export async function downloadAndValidateDriveBackup(accessToken: string, fileId: string): Promise<BackupValidationResult> {
  const text = await driveDownloadFile(accessToken, fileId);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { valid: false, errors: ['This Drive backup could not be read as valid JSON.'] };
  }
  return validateBackupFile(parsed);
}

// ---------------------------------------------------------------------------
// Retention (Stage 3D)
// ---------------------------------------------------------------------------

/** How many app-owned backups to keep in the Drive folder (newest first). */
export const BACKUP_RETENTION_COUNT = 5;

/**
 * A file may be considered for deletion ONLY if ALL of these hold:
 *   - it is directly inside the dedicated backup folder,
 *   - it is a JSON file (the backup file type),
 *   - it carries this app's appProperties marker (i.e. this app created it),
 *   - it has a usable createdTime (we never delete what we cannot order).
 * The file NAME is deliberately not part of the test in either direction:
 * a similar-looking name must never make a file eligible, and identity
 * comes from Drive's own metadata rather than string matching.
 */
export function isRetentionEligible(file: DriveFile, folderId: string): boolean {
  return (
    file.mimeType === BACKUP_MIME_TYPE &&
    file.appProperties?.[APP_PROPERTY_KEY] === APP_PROPERTY_VALUE &&
    (file.parents ?? []).includes(folderId) &&
    typeof file.createdTime === 'string' &&
    !Number.isNaN(Date.parse(file.createdTime))
  );
}

/**
 * Pure decision logic: which files should retention delete? Keeps the
 * just-uploaded backup PLUS the (keepNewest - 1) newest other eligible
 * backups — i.e. exactly `keepNewest` in total — and returns the rest.
 *
 *  - Ordering is by Drive's createdTime (ties broken by file id so the
 *    result is deterministic); filenames are never parsed.
 *  - Files are identified by Drive file id, so duplicate filenames
 *    cannot confuse it.
 *  - The just-uploaded file is never returned, even if the listing has
 *    not caught up and does not include it yet — its upload was already
 *    verified, so it always occupies one of the kept slots.
 *  - Ineligible files (unrelated, other apps', wrong type, other folder)
 *    are never returned no matter how old they are.
 */
export function selectBackupsToDelete(
  files: DriveFile[],
  folderId: string,
  justUploadedFileId: string,
  keepNewest: number = BACKUP_RETENTION_COUNT
): DriveFile[] {
  const others = files
    .filter((f) => f.id !== justUploadedFileId && isRetentionEligible(f, folderId))
    .sort((a, b) => {
      const byTime = Date.parse(b.createdTime as string) - Date.parse(a.createdTime as string);
      return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
    });
  return others.slice(Math.max(0, keepNewest - 1));
}

export interface RetentionResult {
  /** not_needed: nothing to delete. completed: all deletions succeeded. partial: some failed. failed: nothing could be listed or deleted. */
  status: 'not_needed' | 'completed' | 'partial' | 'failed';
  deletedCount: number;
  failedCount: number;
}

/**
 * Enforces retention AFTER a verified successful upload. Never throws:
 * any problem is reported in the result, because a cleanup problem must
 * never turn a successful backup into a failed one. Each deletion is
 * attempted at most once — no retries, and a failure never widens the
 * set of files being deleted.
 */
export async function applyRetention(
  accessToken: string,
  folderId: string,
  justUploadedFileId: string,
  keepNewest: number = BACKUP_RETENTION_COUNT
): Promise<RetentionResult> {
  let files: DriveFile[];
  try {
    // Newest first, so if the folder ever holds more than one page of
    // files the visible page still contains the newest ones and the
    // "keep newest" decision stays correct (older files are simply
    // cleaned up on a later run).
    files = await driveFilesList(
      accessToken,
      `'${folderId}' in parents and trashed = false`,
      'files(id,name,mimeType,createdTime,appProperties,parents)',
      { orderBy: 'createdTime desc', pageSize: 100 }
    );
  } catch {
    return { status: 'failed', deletedCount: 0, failedCount: 0 };
  }

  const toDelete = selectBackupsToDelete(files, folderId, justUploadedFileId, keepNewest);
  if (toDelete.length === 0) {
    return { status: 'not_needed', deletedCount: 0, failedCount: 0 };
  }

  let deletedCount = 0;
  let failedCount = 0;
  for (const file of toDelete) {
    try {
      await driveDeleteFile(accessToken, file.id);
      deletedCount += 1;
    } catch {
      failedCount += 1;
    }
  }

  if (failedCount === 0) return { status: 'completed', deletedCount, failedCount };
  return { status: deletedCount === 0 ? 'failed' : 'partial', deletedCount, failedCount };
}

export interface DriveBackupWithRetentionResult {
  upload: DriveBackupUploadResult;
  retention: RetentionResult;
}

/**
 * The one entry point for "upload a backup and enforce retention" — used
 * by both the manual button and automatic backup. The order is fixed:
 * upload -> verify (inside uploadBackupToDrive) -> only then list and
 * delete. If the upload throws, this throws BEFORE any listing or
 * deletion is attempted, so a failed upload can never cost the user an
 * existing backup.
 */
export async function uploadBackupWithRetention(accessToken: string, backup: BackupFile): Promise<DriveBackupWithRetentionResult> {
  const upload = await uploadBackupToDrive(accessToken, backup);
  const retention = await applyRetention(accessToken, upload.folderId, upload.fileId);
  return { upload, retention };
}
